<template>
  <div class="event-layout">
    <EventSidebar :event-id="eventId" />

    <main class="event-main">
      <div class="page-header">
        <div>
          <p class="breadcrumb">Events → {{ eventName }}</p>
          <h1 class="page-title">Exports</h1>
          <p class="page-subtitle">Download event lists, review links, and pronunciation audio.</p>
        </div>
        <router-link :to="participantsPath" class="secondary-button export-link">
          Participants
        </router-link>
      </div>

      <div v-if="error" class="notice error">{{ error }}</div>
      <div v-if="successMessage" class="notice success">{{ successMessage }}</div>

      
      <section class="panel">
        <div class="export-grid">
          <article class="export-row">
            <div>
              <h2 class="export-title">Attending Queue CSV</h2>
              <p class="panel-copy">Queued students and presenter notes with names, awards, phonetics, notes, and check status.</p>
            </div>
            <button class="primary-button" type="button" @click="downloadExport('attending-students.csv')" :disabled="exporting">
              Download
            </button>
          </article>

          <article class="export-row">
            <div>
              <h2 class="export-title">Excluded Students CSV</h2>
              <p class="panel-copy">Students not marked for the queue, with names, awards, phonetics, notes, and check status.</p>
            </div>
            <button class="primary-button" type="button" @click="downloadExport('excluded-students.csv')" :disabled="exporting">
              Download
            </button>
          </article>

          <article class="export-row">
            <div>
              <h2 class="export-title">Review Links CSV</h2>
              <p class="panel-copy">Student emails, review links, collection status, and pronunciation review fields.</p>
            </div>
            <button class="primary-button" type="button" @click="downloadExport('pronunciation-links.csv')" :disabled="exporting">
              Download
            </button>
          </article>

          <article class="export-row">
            <div>
              <h2 class="export-title">Pronunciation Audio ZIP</h2>
              <p class="panel-copy">Active pronunciation audio for queued students, with a manifest for missing files.</p>
              <p v-if="audioExportJob" class="status-copy">Status: {{ audioExportStatusLabel }}</p>
            </div>
            <button class="primary-button" type="button" @click="startAudioExport" :disabled="exporting || audioExporting">
              {{ audioExporting ? "Preparing..." : "Prepare ZIP" }}
            </button>
          </article>
        </div>
      </section>

      <section class="panel">
        <div class="advanced-export-header">
          <div>
            <h2 class="panel-title">Advanced CSV</h2>
            <p class="panel-copy">Choose a row filter and the columns to include.</p>
          </div>
          <button
            class="primary-button"
            type="button"
            @click="downloadCustomExport"
            :disabled="exporting || !advancedExportColumns.length"
          >
            {{ exporting ? "Exporting..." : "Download CSV" }}
          </button>
        </div>

        <div class="advanced-export-controls">
          <label>
            <span class="field-label">Rows</span>
            <select v-model="advancedExportFilter" class="field-input">
              <option v-for="option in advancedExportFilters" :key="option.value" :value="option.value">
                {{ option.label }}
              </option>
            </select>
          </label>
        </div>

        <div class="advanced-column-grid">
          <label
            v-for="column in customExportColumnOptions"
            :key="column.value"
            class="column-check"
          >
            <input
              type="checkbox"
              :checked="advancedExportColumns.includes(column.value)"
              @change="toggleAdvancedExportColumn(column.value)"
            />
            <span>{{ column.label }}</span>
          </label>
        </div>
      </section>
    </main>
  </div>
</template>

<script>
import {
  downloadCustomEventExport,
  downloadEventExport,
  fetchAudioExportJob,
  fetchEvent,
  startAudioExportJob,
} from "@/lib/api";
import EventSidebar from "./EventSidebar.vue";

const ADVANCED_EXPORT_DEFAULT_COLUMNS = [
  "queueOrder",
  "participantId",
  "studentIdNumber",
  "type",
  "fullName",
  "text",
  "award",
  "activePhonetics",
  "oratorNotes",
  "checkStatus",
  "pronunciationStatus",
  "collectionStatus",
];
const ADVANCED_EXPORT_STANDARD_COLUMNS = [
  { value: "queueOrder", label: "Queue Order" },
  { value: "participantId", label: "Participant ID" },
  { value: "studentIdNumber", label: "Student Number" },
  { value: "type", label: "Type" },
  { value: "fullName", label: "Full Name" },
  { value: "text", label: "Text" },
  { value: "award", label: "Award" },
  { value: "includedInQueue", label: "Marked For Queue" },
  { value: "email", label: "Email" },
  { value: "reviewLink", label: "Review Link" },
  { value: "reviewTokenExpiresAt", label: "Review Token Expires" },
  { value: "reviewTokenUsedAt", label: "Review Token Used" },
  { value: "reviewLinkLastSentAt", label: "Review Link Last Sent" },
  { value: "activePhonetics", label: "Active Phonetics" },
  { value: "generatedPhonetics", label: "Generated Phonetics" },
  { value: "manualPhonetics", label: "Manual Phonetics" },
  { value: "oratorNotes", label: "Orator Notes" },
  { value: "checkStatus", label: "Check Status" },
  { value: "pronunciationStatus", label: "Pronunciation Status" },
  { value: "collectionStatus", label: "Collection Status" },
  { value: "selectedAlternativePhonetics", label: "Selected Alternative Phonetics" },
  { value: "reviewStatus", label: "Review Status" },
];
const ADVANCED_EXPORT_FILTERS = [
  { value: "attending", label: "Attending only" },
  { value: "all", label: "All rows" },
  { value: "students", label: "Students only" },
  { value: "needs_review", label: "Needs review" },
  { value: "check_requested", label: "Please check names" },
  { value: "missing_pronunciation", label: "Missing pronunciation" },
];
const PRIORITY_EXTRA_COLUMNS = [
  "firstname",
  "lastname",
  "student_seat_number",
  "student_seat_numbe",
  "externalid",
  "external_id",
  "prefix",
  "number",
  "email",
];

export default {
  name: "EventExportsView",
  components: {
    EventSidebar,
  },
  data() {
    return {
      snapshot: null,
      exporting: false,
      audioExporting: false,
      audioExportJob: null,
      audioExportPollingTimer: null,
      advancedExportFilter: "attending",
      advancedExportColumns: [...ADVANCED_EXPORT_DEFAULT_COLUMNS],
      advancedExportFilters: ADVANCED_EXPORT_FILTERS,
      error: "",
      successMessage: "",
    };
  },
  computed: {
    eventId() {
      return this.$route.params.eventId;
    },
    eventName() {
      return this.snapshot?.event?.name || this.eventId;
    },
    participantsPath() {
      return `/events/${encodeURIComponent(this.eventId)}/participants`;
    },
    orderedExtraColumns() {
      const columns = new Set(this.snapshot?.participants?.columns || []);
      (this.snapshot?.participants?.items || []).forEach((item) => {
        Object.keys(item.extraFields || {}).forEach((column) => columns.add(column));
      });
      const priorityMap = new Map(PRIORITY_EXTRA_COLUMNS.map((column, index) => [column, index]));
      return [...columns].sort((left, right) => {
        const leftPriority = priorityMap.has(left.toLowerCase()) ? priorityMap.get(left.toLowerCase()) : 999;
        const rightPriority = priorityMap.has(right.toLowerCase()) ? priorityMap.get(right.toLowerCase()) : 999;
        if (leftPriority !== rightPriority) {
          return leftPriority - rightPriority;
        }
        return left.localeCompare(right);
      });
    },
    customExportColumnOptions() {
      const extraColumns = this.orderedExtraColumns.map((column) => ({
        value: `extra:${column}`,
        label: `Imported: ${column}`,
      }));
      return [...ADVANCED_EXPORT_STANDARD_COLUMNS, ...extraColumns];
    },
    audioExportStatusLabel() {
      const job = this.audioExportJob || {};
      const labels = {
        queued: "queued",
        processing: "processing",
        completed: "ready",
        partial_failed: "ready with missing audio listed in the manifest",
        failed: "failed",
      };
      const label = labels[job.status] || job.status || "not started";
      if (["completed", "partial_failed", "failed"].includes(job.status)) {
        return `${label}.`;
      }
      return `${label} (${job.processed || 0}/${job.total || 0}).`;
    },
  },
  async mounted() {
    await this.loadEvent();
  },
  beforeUnmount() {
    this.clearAudioExportPolling();
  },
  methods: {
    async loadEvent() {
      try {
        this.snapshot = await fetchEvent(this.eventId);
      } catch (error) {
        this.error = "Unable to load event exports.";
      }
    },
    async downloadExport(exportName) {
      this.exporting = true;
      this.error = "";
      this.successMessage = "";
      try {
        const blob = await downloadEventExport(this.eventId, exportName);
        this.downloadBlob(blob, exportName);
        this.successMessage = "Export downloaded.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to download export.";
      } finally {
        this.exporting = false;
      }
    },
    async downloadCustomExport() {
      this.exporting = true;
      this.error = "";
      this.successMessage = "";
      try {
        const blob = await downloadCustomEventExport(this.eventId, {
          filter: this.advancedExportFilter,
          columns: this.advancedExportColumns,
        });
        this.downloadBlob(blob, "custom-export.csv");
        this.successMessage = "Export downloaded.";
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to download custom export.";
      } finally {
        this.exporting = false;
      }
    },
    async startAudioExport() {
      this.audioExporting = true;
      this.error = "";
      this.successMessage = "";
      try {
        const result = await startAudioExportJob(this.eventId);
        this.audioExportJob = result.job;
        this.successMessage = "Audio ZIP export started.";
        if (result.job?.jobId) {
          this.scheduleAudioExportPolling(result.job.jobId);
        }
      } catch (error) {
        this.error = error?.response?.data?.error || "Unable to start audio ZIP export.";
        this.audioExporting = false;
      }
    },
    scheduleAudioExportPolling(jobId) {
      this.clearAudioExportPolling();
      this.audioExportPollingTimer = window.setTimeout(() => {
        this.pollAudioExportJob(jobId);
      }, 2000);
    },
    clearAudioExportPolling() {
      if (this.audioExportPollingTimer) {
        window.clearTimeout(this.audioExportPollingTimer);
        this.audioExportPollingTimer = null;
      }
    },
    async pollAudioExportJob(jobId) {
      try {
        const result = await fetchAudioExportJob(this.eventId, jobId);
        this.audioExportJob = result.job;
        if (["completed", "partial_failed"].includes(result.job?.status)) {
          this.clearAudioExportPolling();
          this.audioExporting = false;
          if (result.download?.url) {
            this.downloadUrl(result.download.url);
            this.successMessage = "Audio ZIP export downloaded.";
          }
          return;
        }
        if (result.job?.status === "failed") {
          this.clearAudioExportPolling();
          this.audioExporting = false;
          this.error = "Audio ZIP export failed.";
          return;
        }
        this.scheduleAudioExportPolling(jobId);
      } catch (error) {
        this.clearAudioExportPolling();
        this.audioExporting = false;
        this.error = "Unable to check audio ZIP export.";
      }
    },
    downloadBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    },
    downloadUrl(url) {
      const link = document.createElement("a");
      link.href = url;
      link.download = "pronunciation-audio.zip";
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    toggleAdvancedExportColumn(column) {
      if (this.advancedExportColumns.includes(column)) {
        this.advancedExportColumns = this.advancedExportColumns.filter((item) => item !== column);
        return;
      }
      this.advancedExportColumns = [...this.advancedExportColumns, column];
    },
  },
};
</script>

<style scoped>
.event-layout {
  display: flex;
  gap: 1rem;
  width: calc(100vw - 0.25rem);
  max-width: none;
  box-sizing: border-box;
  margin: 0 auto;
  padding: 0 1rem 2rem;
}

.event-main {
  flex: 1;
  min-width: 0;
}

.page-header,
.advanced-export-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
  margin-bottom: 1rem;
}

.breadcrumb,
.page-subtitle,
.panel-copy,
.status-copy {
  color: var(--orator-graphite);
}

.page-title,
.panel-title,
.export-title {
  margin: 0;
  color: var(--orator-navy);
}

.panel {
  background: white;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.75rem;
  box-shadow: var(--orator-shadow);
  padding: 1rem;
  margin-bottom: 1rem;
}

.export-grid {
  display: grid;
  gap: 0.75rem;
}

.export-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  padding: 1rem;
}

.export-title {
  font-size: 1rem;
}

.status-copy {
  margin: 0.35rem 0 0;
  font-weight: 700;
}

.advanced-export-controls {
  max-width: 22rem;
  margin-bottom: 1rem;
}

.advanced-column-grid {
  display: grid;
  gap: 0.5rem;
  grid-template-columns: repeat(auto-fit, minmax(210px, 1fr));
}

.column-check {
  display: inline-flex;
  align-items: center;
  gap: 0.45rem;
  min-height: 2.2rem;
  border: 1px solid var(--orator-smoke);
  border-radius: 0.5rem;
  padding: 0.35rem 0.55rem;
  background: white;
  color: var(--orator-arsenic);
  font-weight: 650;
}

.field-label {
  display: block;
  margin-bottom: 0.35rem;
  color: var(--orator-arsenic);
  font-size: 0.85rem;
  font-weight: 700;
}

.field-input {
  display: block;
  width: 100%;
  border-radius: 0.45rem;
  border: 1px solid var(--orator-steel);
  background: white;
  padding: 0.75rem 0.9rem;
  color: var(--orator-navy);
}

.primary-button,
.secondary-button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  border-radius: 0.5rem;
  padding: 0.75rem 1rem;
  font-weight: 800;
  text-decoration: none;
}

.primary-button {
  border: 1px solid var(--orator-blue);
  background: var(--orator-blue);
  color: white;
}

.secondary-button {
  border: 1px solid var(--orator-steel);
  background: white;
  color: var(--orator-arsenic);
}

.notice {
  border-radius: 0.5rem;
  padding: 0.85rem 1rem;
  margin-bottom: 1rem;
  font-weight: 700;
}

.notice.error {
  background: #fff1f0;
  border: 1px solid #ffccc7;
  color: #a8071a;
}

.notice.success {
  background: #f6ffed;
  border: 1px solid #b7eb8f;
  color: #237804;
}
</style>
